import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Card, CardContent, Checkbox, Chip, FormControlLabel, Grid, Paper, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { toast } from 'react-toastify';
import DateRangeFilter from '../common/DateRangeFilter';
import { Report } from '../../models/report';
import { getCurrentRole } from '../../utils/auth';
import { DIMENSION_LABELS, formatPeriodLabel } from '../../utils/periodLabels';
import { formatMillions, formatVND } from '../../utils/money';
import * as vn from '../../utils/vnDate';

const ORDER_TYPE_LABELS = { '': 'Tất cả', INSTANT_ORDER: 'Đơn lẻ', MONTHLY_PACKAGE_ORDER: 'Gói tháng' };
const COLORS = { instant: '#5B8DB8', monthly: '#F0A050', shipping: '#9E9E9E' };

// Quick ranges, all in Vietnam calendar dates.
const PRESETS = [
  { key: 'today', label: 'Hôm nay', range: () => [vn.todayVN(), vn.todayVN()] },
  { key: '7d', label: '7 ngày', range: () => [vn.daysAgoVN(6), vn.todayVN()] },
  { key: 'month', label: 'Tháng này', range: () => [vn.startOfMonthVN(), vn.todayVN()] },
  { key: 'last-month', label: 'Tháng trước', range: () => vn.lastMonthRangeVN() },
  { key: 'quarter', label: 'Quý này', range: () => [vn.startOfQuarterVN(), vn.todayVN()] },
  { key: 'year', label: 'Năm nay', range: () => [vn.startOfYearVN(), vn.todayVN()] },
];

const Kpi = ({ label, value, color }) => (
  <Grid item xs={6} sm={4} md={2}>
    <Card variant="outlined" sx={{ height: '100%' }} role="group" aria-label={label}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" sx={{ color, fontWeight: 600, whiteSpace: 'nowrap' }}>{value}</Typography>
      </CardContent>
    </Card>
  </Grid>
);

const RevenueReport = ({ setPageTitle }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = getCurrentRole() === 'ADMIN';

  // Filters live in the URL so a view can be shared / reloaded.
  const startDate = searchParams.get('start') || vn.startOfMonthVN();
  const endDate = searchParams.get('end') || vn.todayVN();
  const dimension = searchParams.get('dim') || 'day';
  const orderType = searchParams.get('type') || '';
  const update = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setSearchParams(next);
  };

  const [includeShipping, setIncludeShipping] = useState(true);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isAdmin) navigate('/', { replace: true });
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (setPageTitle) setPageTitle('Báo cáo doanh thu');
  }, [setPageTitle]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const result = await Report.fetch('revenue', {
          start_date: startDate,
          end_date: endDate,
          dimension,
          ...(orderType && { order_type: orderType }),
        });
        if (!cancelled) setData(result);
      } catch (e) {
        if (!cancelled) {
          setData(null);
          toast.error('Không thể tải báo cáo doanh thu');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isAdmin, startDate, endDate, dimension, orderType]);

  // Everything shown derives from the loaded series; the shipping toggle is client-side.
  const view = useMemo(() => {
    if (!data) return null;
    const totalOf = (p) => (includeShipping ? p.total : p.total - p.shipping);
    const s = data.summary;
    const total = totalOf(s);
    return {
      summary: { ...s, total, avg: s.orders ? Math.round(total / s.orders) : 0 },
      rows: (data.series || []).map((p) => ({
        ...p,
        label: formatPeriodLabel(p.period, dimension),
        total: totalOf(p),
        avg: p.orders ? Math.round(totalOf(p) / p.orders) : 0,
      })),
    };
  }, [data, includeShipping, dimension]);

  if (!isAdmin) return null;

  return (
    <Box sx={{ maxWidth: 1200, margin: '24px auto', px: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={(v) => update({ start: v })}
          onEndDateChange={(v) => update({ end: v })}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          {PRESETS.map((p) => (
            <Chip key={p.key} label={p.label} size="small" variant="outlined"
              onClick={() => { const [s, e] = p.range(); update({ start: s, end: e }); }} />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">Nhóm theo</Typography>
        <ToggleButtonGroup value={dimension} exclusive size="small"
          onChange={(e, v) => v && update({ dim: v })}>
          {Object.entries(DIMENSION_LABELS).map(([key, label]) => (
            <ToggleButton key={key} value={key}>{label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">Loại đơn</Typography>
        <ToggleButtonGroup value={orderType} exclusive size="small"
          onChange={(e, v) => v !== null && update({ type: v })}>
          {Object.entries(ORDER_TYPE_LABELS).map(([key, label]) => (
            <ToggleButton key={key || 'all'} value={key}>{label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <FormControlLabel
          control={<Checkbox size="small" checked={includeShipping}
            onChange={(e) => setIncludeShipping(e.target.checked)} />}
          label="Gồm phí vận chuyển"
        />
      </Box>

      {loading || !view ? (
        <Skeleton variant="rectangular" width="100%" height={420} />
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Kpi label="Tổng doanh thu" value={formatVND(view.summary.total)} color="primary.main" />
            <Kpi label="Đơn lẻ" value={formatVND(view.summary.instant)} color={COLORS.instant} />
            <Kpi label="Gói tháng" value={formatVND(view.summary.monthly)} color={COLORS.monthly} />
            <Kpi label="Phí vận chuyển" value={formatVND(view.summary.shipping)} color={COLORS.shipping} />
            <Kpi label="Số đơn" value={view.summary.orders.toLocaleString('vi-VN')} />
            <Kpi label="TB / đơn" value={formatVND(view.summary.avg)} />
          </Grid>

          {view.summary.unpriced_lines > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {view.summary.unpriced_lines} dòng gói tháng không có hệ số quy đổi và được tính theo giá lẻ.
            </Alert>
          )}

          {view.summary.orders === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                Không có doanh thu trong khoảng thời gian đã chọn.
              </Typography>
            </Paper>
          ) : (
            <>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Doanh thu theo {DIMENSION_LABELS[dimension].toLowerCase()}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={view.rows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" fontSize={12}
                      angle={dimension === 'day' ? -45 : 0}
                      textAnchor={dimension === 'day' ? 'end' : 'middle'}
                      height={dimension === 'day' ? 80 : 40} />
                    <YAxis tickFormatter={formatMillions} width={70} />
                    <Tooltip formatter={(value, name) => [formatVND(value), name]} />
                    <Legend />
                    <Bar dataKey="instant" name="Đơn lẻ" stackId="a" fill={COLORS.instant} />
                    <Bar dataKey="monthly" name="Gói tháng" stackId="a" fill={COLORS.monthly} />
                    {includeShipping && (
                      <Bar dataKey="shipping" name="Phí vận chuyển" stackId="a" fill={COLORS.shipping} />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </Paper>

              <TableContainer component={Paper}>
                <Table size="small" aria-label="Bảng doanh thu theo kỳ">
                  <TableHead>
                    <TableRow>
                      <TableCell>Kỳ</TableCell>
                      <TableCell align="right">Đơn lẻ</TableCell>
                      <TableCell align="right">Gói tháng</TableCell>
                      <TableCell align="right">Phí vận chuyển</TableCell>
                      <TableCell align="right">Tổng</TableCell>
                      <TableCell align="right">Số đơn</TableCell>
                      <TableCell align="right">TB / đơn</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {view.rows.map((r) => (
                      <TableRow key={r.period} hover>
                        <TableCell>{r.label}</TableCell>
                        <TableCell align="right">{formatVND(r.instant)}</TableCell>
                        <TableCell align="right">{formatVND(r.monthly)}</TableCell>
                        <TableCell align="right">{formatVND(r.shipping)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatVND(r.total)}</TableCell>
                        <TableCell align="right">{r.orders}</TableCell>
                        <TableCell align="right">{formatVND(r.avg)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default RevenueReport;
